import Head from 'next/head';
import { useRouter } from 'next/router';

// UI
import { BlogPostCard } from '@virginexperiencedays/components-v2/src/cards/BlogPostCard';
import { Breadcrumbs } from '@virginexperiencedays/components-v2/src/navigation/Breadcrumbs';
import { Divider } from '@virginexperiencedays/components-v2/src/layout/Divider';
import { Newsletter } from '@virginexperiencedays/components-v2/src/layout/Newsletter';
import { VisualHeading } from '@virginexperiencedays/components-v2/src/typography/VisualHeading';
import { AuthorBioModule } from '@virginexperiencedays/components-v2/src/pages/blog/AuthorBioModule';
import { Pagination } from '@virginexperiencedays/components-v2/src/layout/Pagination';
import { Content } from '@virginexperiencedays/components-v2/src/cms/Content';
import { Grid } from '@virginexperiencedays/components-v2/src/layout/Grid';
import { EditorsPickRenderer as EditorsPick } from './search/ProductCard';

// types
import type { BlogAuthorPageType, BlogPostPageType } from '@virginexperiencedays/cms';
import type { ProductMapType, Product } from '@virginexperiencedays/products';

// SEO
import blogListingAuthorPageStructuredData from '../SEO/StructuredData/blogListingAuthorPage';
import SEO from '../SEO/SEO';
import { GLOBAL_SETTINGS } from '../tracking/gtm/consts';

// utils
import DynamicYield from '../DynamicYield/DynamicYield';
import { DyContextPageType } from '@virginexperiencedays/dy';
import { fallbackImage } from '../../utils/fallbackImage';
import { SIGNUP_SOURCE, submit } from '../../utils/newsletter';
import { buildMetaRobotsContent } from '../SEO/utils/buildMetaRobotsContent';

// hooks
import { useTracking } from '../tracking/useTracking';
import { pageView as trackFbPageView } from '../tracking/fb/events';

type BlogAuthorPageProps = {
  blog_author_page: BlogAuthorPageType;
  authorPosts: BlogPostPageType[];
  commonCrumbs: { name: string; path?: string }[];
  products: ProductMapType;
  currentPage: number;
  totalPosts: number;
  postsPerPage: number;
  isLandingPage: boolean;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

function BlogAuthorPage({
  blog_author_page,
  authorPosts,
  commonCrumbs,
  products,
  currentPage,
  totalPosts,
  postsPerPage,
  isLandingPage,
  hasNextPage,
  hasPrevPage,
}: BlogAuthorPageProps) {
  const router = useRouter();
  const pathname = router?.pathname;

  useTracking({
    legacy: { pageType: 'Blog Listing' },
    gtm: { initial: [GLOBAL_SETTINGS, { ecommerce: null }] },
    fb: () => {
      trackFbPageView({
        name: `Articles By ${blog_author_page.name}`,
      });
    },
  });

  const editorsPicks = blog_author_page?.editors_picks
    .filter((editors_pick) => editors_pick?.product_id)
    ?.map(({ product_id }) => {
      return { ...products[product_id] };
    })
    .slice(0, 4);

  const invalid = editorsPicks.map((product) => validate(product));

  if (invalid.length)
    console.warn(`blog author page '${blog_author_page?._meta?.uid}' has invalid products`);

  return (
    <div>
      <Head>
        <link rel="preconnect" href="https://images.prismic.io/" />
        {process.env.NEXT_PUBLIC_HOME_LINK ? (
          currentPage === 1 ? (
            <link
              rel="canonical"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${blog_author_page._meta?.uid}`}
            />
          ) : (
            <link
              rel="canonical"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${blog_author_page._meta?.uid}?page=${currentPage}`}
            />
          )
        ) : null}
        {hasPrevPage &&
          (currentPage === 2 ? (
            <link
              rel="prev"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${blog_author_page._meta?.uid}`}
            />
          ) : (
            <link
              rel="prev"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${
                blog_author_page._meta?.uid
              }?page=${currentPage - 1}`}
            />
          ))}
        {hasNextPage && (
          <link
            rel="next"
            href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${
              blog_author_page._meta?.uid
            }?page=${currentPage + 1}`}
          />
        )}
      </Head>
      <SEO
        {...blog_author_page}
        og_image={blog_author_page.og_image ?? fallbackImage.OpenGraph}
        meta_title={
          currentPage === 1
            ? blog_author_page.meta_title ?? `${blog_author_page.name} | Virgin Experience Days`
            : blog_author_page.meta_title ??
              `${blog_author_page.name} - Page ${currentPage} | Virgin Experience Days`
        }
        meta_description={
          blog_author_page.meta_description ??
          `Discover more articles from ${blog_author_page.name} here.`
        }
        structuredData={blogListingAuthorPageStructuredData({
          name: blog_author_page.name,
          urlSlug: blog_author_page._meta?.uid,
          baseUrl: `experience-blog`,
          totalPosts,
          currentPage,
          postsPerPage,
          breadcrumbs: commonCrumbs
            .concat([
              {
                name: `${blog_author_page.name}`,
                path: `/experience-blog/${blog_author_page._meta?.uid}`,
              },
            ])
            .filter((crumb) => crumb.name)
            .map((crumb) => ({ name: crumb.name, url: crumb.path })),
          abstract: `Articles By ${blog_author_page.name}`,
          collectionItems: authorPosts
            ? authorPosts
                .flat()
                .filter((item) => item.title)
                .map((item) => ({
                  name: item.title,
                  url: `/experience-blog/${item._meta?.uid}`,
                }))
            : null,
        })}
        meta_robots={buildMetaRobotsContent(blog_author_page?.meta_robots)}
      />
      <DynamicYield type={DyContextPageType.OTHER} data={pathname ?? []} />
      <Content className="py-8">
        <Breadcrumbs
          crumbs={commonCrumbs.concat([
            {
              name: `${blog_author_page?.name}`,
            },
          ])}
        />
        {blog_author_page.name && (
          <VisualHeading
            className="mb-6 mt-10 leading-tight text-neutral-strong sm:text-5xl sm:leading-tight"
            size="1"
          >
            Articles By {blog_author_page.name}
          </VisualHeading>
        )}
        {isLandingPage && blog_author_page.name && (
          <AuthorBioModule
            className="rounded-lg"
            imageClassName="rounded-full overflow-hidden"
            author={blog_author_page}
          />
        )}
        {authorPosts && (
          <Grid
            space="lg"
            spaceY={{
              mobile: 'xl',
              desktop: '3xl',
            }}
            columns={{
              mobile: 1,
              largeMobile: 2,
              desktop: 3,
            }}
          >
            {authorPosts.map((post, index) => (
              <BlogPostCard key={index} post={post} href={post._meta?.uid} />
            ))}
          </Grid>
        )}
        {authorPosts && (
          <Pagination
            className="my-12 md:my-20"
            itemsCount={totalPosts}
            perPage={postsPerPage}
            currentPage={currentPage}
            renderLink={(page) =>
              page === 1 || !page
                ? `/experience-blog/${blog_author_page._meta?.uid}`
                : `/experience-blog/${blog_author_page._meta?.uid}?page=${page}`
            }
          />
        )}
      </Content>
      <Content className="py-8">
        <EditorsPick
          trackClassName="children:md:w-[calc(25%_-_12px)] children:lg:w-[calc(25%_-_12px)] children:xl:w-[calc(25%_-_12px)] lg:max-w-full"
          cardImageClassName="aspect-[18/13]"
          title="Editors Picks"
          description={
            blog_author_page.editors_picks_description &&
            blog_author_page.editors_picks_description
              .filter((description) => description.text)
              .map((description) => description.text)
          }
          productsList={
            blog_author_page.editors_picks &&
            blog_author_page.editors_picks
              .filter((editors_pick) => editors_pick.product_id)
              ?.map(({ product_id }) => {
                return { ...products[product_id] };
              })
              .filter(
                (product) =>
                  product.title &&
                  product.url &&
                  product.media?.mainImage &&
                  product.status === 'active'
              )
              .slice(0, 4)
          }
        />
      </Content>
      <Content className="py-8">
        <Divider variant="grey" />
        <Newsletter
          onSubmit={submit}
          className="mt-12"
          signUpSource={SIGNUP_SOURCE.blogAuthor}
          roundedLeftButton={false}
        />
      </Content>
    </div>
  );
}

function validate(product: Product) {
  const { id, title, url, price, media, slug } = product;

  if (!title || !url || !price?.displayPrice || !price?.rrp || !media?.images?.length || !slug)
    return id;
}

export default BlogAuthorPage;
